import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useRestaurantsService, IndexRestaurantsMap } from '@restworld/data-services';
import { trackPlacesUsage } from '@restworld/utils-common';
import { Restaurant } from '@restworld/utility-types';

const GOOGLE_PLACES_API_KEY = process.env['NEXT_PUBLIC_NEW_GOOGLE_PLACES_API_KEY'] || '';
const NEARBY_RADIUS_KM = 5;
const GOOGLE_MAX_RESULTS = 10;
const BACKEND_LIMIT = 20;
// Nearby Search (New) is a Pro-SKU billed call (~$32/1k): persist the result per
// coordinates so an employer pays at most one call per day, not one per page view.
// 24h stays within Google's Places-content caching limits.
const NEARBY_CACHE_PREFIX = 'referral-nearby-places';
const NEARBY_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

function nearbyCacheKey(lat: number, lon: number) {
  return `${NEARBY_CACHE_PREFIX}:${lat.toFixed(3)},${lon.toFixed(3)}`;
}

function loadCachedNearbyPlaces(key: string): GoogleNearbyPlace[] | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { ts, places } = JSON.parse(raw) as { ts: number; places: GoogleNearbyPlace[] };
    if (typeof ts === 'number' && Date.now() - ts < NEARBY_CACHE_TTL_MS && Array.isArray(places)) return places;
  } catch {
    // ignore
  }
  return null;
}

function saveCachedNearbyPlaces(key: string, places: GoogleNearbyPlace[]) {
  try {
    localStorage.setItem(key, JSON.stringify({ ts: Date.now(), places }));
  } catch {
    // ignore
  }
}

/** Shape returned by Google Places Nearby Search (New) */
interface GoogleNearbyPlace {
  id: string;
  displayName?: { text: string; languageCode?: string };
  primaryTypeDisplayName?: { text: string; languageCode?: string };
}

interface GoogleNearbyResponse {
  places?: GoogleNearbyPlace[];
}

export interface NearbyVenue {
  placeId: string;
  name: string;
  type: string;
}

/**
 * Fetches nearby venues that are NOT in Restworld's DB.
 *
 * 1. Get employer's most recent restaurant → lat/lon
 * 2. In parallel:
 *    a) Backend: fetch nearby DB restaurants (with gmaps_place_id) within radius
 *    b) Google: Nearby Search (New) for establishments within same radius
 * 3. Filter out Google results whose place ID matches any backend restaurant's gmaps_place_id
 * 4. Return first 3 non-Restworld venues
 */
export function useNearbyVenues({ enabled = true }: { enabled?: boolean } = {}) {
  const restaurantsService = useRestaurantsService();

  // Step 1: Fetch employer's most recent restaurant to get coordinates
  const { data: employerRestaurant, isLoading: isLoadingEmployer } = useQuery({
    queryKey: ['referral-employer-restaurant'],
    queryFn: () => restaurantsService.fetchRestaurants({ limit: 1 }),
    enabled,
    select: (res): Restaurant | null => {
      const pageData = res.data;
      // IndexRestaurantsMap shape: { hits, data: [{ restaurant, ... }] }
      if (pageData && typeof pageData === 'object' && 'data' in pageData && 'hits' in pageData) {
        const mapData = pageData as IndexRestaurantsMap;
        return mapData.data?.[0]?.restaurant ?? null;
      }
      // Plain array shape: Restaurant[]
      if (Array.isArray(pageData) && pageData.length > 0) {
        return pageData[0] as Restaurant;
      }
      return null;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const lat = employerRestaurant?.address_lat;
  const lon = employerRestaurant?.address_lon;
  const hasCoordinates = typeof lat === 'number' && typeof lon === 'number' && lat !== 0 && lon !== 0;

  // Step 2a: Backend — fetch nearby Restworld restaurants within radius
  const { data: dbRestaurants, isLoading: isLoadingDb } = useQuery({
    queryKey: ['referral-nearby-db-restaurants', lat, lon],
    queryFn: () =>
      restaurantsService.fetchRestaurants({
        limit: BACKEND_LIMIT,
        offset: 0,
        filters: {
          address_lat: lat,
          address_lon: lon,
          radius: NEARBY_RADIUS_KM,
        },
      }),
    select: (res): Restaurant[] => {
      const pageData = res.data;
      // IndexRestaurantsMap shape: { hits, data: [{ restaurant, ... }] }
      if (pageData && typeof pageData === 'object' && 'data' in pageData && 'hits' in pageData) {
        const mapData = pageData as IndexRestaurantsMap;
        return mapData.data?.map((item) => item.restaurant) ?? [];
      }
      // Plain array shape: Restaurant[]
      if (Array.isArray(pageData)) {
        return pageData as Restaurant[];
      }
      return [];
    },
    enabled: enabled && hasCoordinates,
    staleTime: 10 * 60 * 1000,
  });

  // Step 2b: Google — Nearby Search (New) for establishments
  const { data: googlePlaces, isLoading: isLoadingGoogle } = useQuery({
    queryKey: ['referral-nearby-google-places', lat, lon],
    queryFn: async () => {
      const cacheKey = nearbyCacheKey(lat as number, lon as number);
      const cached = loadCachedNearbyPlaces(cacheKey);
      if (cached) return cached;

      // Only a real (uncached) Google Nearby Search is a billed call, so emit telemetry here.
      trackPlacesUsage({ surface: 'employer_referral_suggested_venues', api: 'nearby_search' });
      const response = await fetch('https://places.googleapis.com/v1/places:searchNearby', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': GOOGLE_PLACES_API_KEY,
          'X-Goog-FieldMask': 'places.id,places.displayName,places.primaryTypeDisplayName',
        },
        body: JSON.stringify({
          includedTypes: ['restaurant'],
          locationRestriction: {
            circle: {
              center: { latitude: lat, longitude: lon },
              radius: NEARBY_RADIUS_KM * 1000,
            },
          },
          maxResultCount: GOOGLE_MAX_RESULTS,
          languageCode: 'it',
        }),
      });

      if (!response.ok) {
        throw new Error(`Google Places API error: ${response.status}`);
      }

      const data: GoogleNearbyResponse = await response.json();
      const places = data.places ?? [];
      saveCachedNearbyPlaces(cacheKey, places);
      return places;
    },
    enabled: enabled && hasCoordinates && !!GOOGLE_PLACES_API_KEY,
    staleTime: 10 * 60 * 1000,
  });

  // Step 3: Filter out venues already in Restworld DB by gmaps_place_id
  const venues: NearbyVenue[] = useMemo(() => {
    if (!googlePlaces || !dbRestaurants) return [];

    const dbPlaceIds = new Set(dbRestaurants.filter((r) => r.gmaps_place_id).map((r) => r.gmaps_place_id));

    return googlePlaces
      .filter((place) => !dbPlaceIds.has(place.id))
      .map((place) => ({
        placeId: place.id,
        name: place.displayName?.text ?? '',
        type: place.primaryTypeDisplayName?.text ?? '',
      }));
  }, [googlePlaces, dbRestaurants]);

  const isLoading = isLoadingEmployer || (hasCoordinates && (isLoadingDb || isLoadingGoogle));
  const hasNoResults = !isLoading && venues.length === 0;

  return { venues, isLoading, hasNoResults, hasCoordinates };
}

export default useNearbyVenues;
